// The ONE declaration of what the app persists.
//
// This file exists because the same field lists were previously written out
// by hand in three places — `partialize` (48 entries), `pickBand` (13), and
// `StyleBand` (the same 13) — and every one of them fails SILENTLY. Add a
// setting and forget `partialize` and it simply doesn't survive a reload; add
// a field to `StyleBand` and forget `pickBand` and it isn't remembered per
// tune. Nothing throws; a user just loses work.
//
// So: declare the keys once, derive everything mechanically, and make the two
// mistakes that matter into COMPILE errors (missing band key) or TEST
// failures (persisted key that isn't real state). This is also the seam the
// storage facade in #176 sits behind — one list of what a store must round
// trip, rather than a growing set of ad-hoc persistence paths.

import type { StyleBand } from '@/music/style';

// NB this module deliberately does NOT import AppState: the store imports
// these lists, so depending on it back would be a cycle that breaks type
// inference across the whole creator. The store asserts the keys are real
// AppState keys on its side, where AppState is already in scope — so the
// guarantee is kept without the cycle.

/**
 * The band subset remembered per tune. Exhaustive over `StyleBand` — the
 * assertion below is a compile error if a field is added to StyleBand and
 * not listed here.
 */
export const BAND_KEYS = [
  'bassline',
  'bassSolo',
  'bassStyle',
  'bassFeel',
  'bassSwing',
  'swingFeel',
  'swingWithKit',
  'melodyPlacement',
  'melodyPhrasing',
  'bassPlacement',
  'bassAccent',
  'bassSkips',
  'compPlacement',
  'swingOffset',
  'melodySwingOffset',
  'compSwingOffset',
  'bassSwingOffset',
  'bassGhosts',
  'bassAnticipate',
  'bassTriplets',
  'bassAmount',
  'bassFollowForm',
  'compInstrument',
  'compPianoFamily',
  'compStyle',
  'compAmount',
  'drums',
  'drumLoopKey',
] as const satisfies readonly (keyof StyleBand)[];

/** A compile error here means a StyleBand field is missing from BAND_KEYS. */
type MissingBandKey = Exclude<keyof StyleBand, (typeof BAND_KEYS)[number]>;
const _bandKeysAreExhaustive: MissingBandKey extends never ? true : MissingBandKey = true;
void _bandKeysAreExhaustive;

/**
 * Everything written to localStorage, grouped by what it is so the list stays
 * readable and a reviewer can see at a glance which bucket a new field joins.
 *
 * NOT persisted, deliberately: `chart` (a projection of `score`, rebuilt at
 * rehydrate), transport/playback runtime, and session-only UI state such as
 * the Tunes browser scope and the entry voice.
 */
export const PERSISTED_KEYS = [
  // --- voicing engine preferences ---
  'defaultVoicingFamily',
  'stringSet',
  'freeStringSet',
  'avoidB9',
  'startingInversion',
  'voiceLeadingEngine',
  'preferredAlternativeIds',
  'scoreViewPart',

  // --- appearance ---
  'theme',
  'sortMode',
  'chartScale',
  'scoreScale',
  'arrangeChordNameScale',
  'arrangeFretNumberScale',
  'arrangeDiagramMarkers',
  'chordSymbolStyle',
  'scoreEdition',
  'diagramInstrument',
  'playheadTint',
  'showLyrics',
  'showBarNumbers',
  'barsPerLine',
  'chordMelodyDensity',
  'showAnalysis',
  'reharmSettings',

  // --- workspace view ---
  'viewSurface',
  'voicingLens',
  'unrollRepeats',
  'cycleForm',
  'sheetPages',
  'tuneMapDetail',
  'tuneMapLayout',
  'entryDuration',

  // --- device ---
  'keepAwake',

  // --- transport + practice ---
  'audioEnabled',
  'tempo',
  'metronome',
  'countIn',
  'countInBars',
  'countInBetweenLoops',
  'masterReverb',
  'masterReverbEnabled',
  'masterCompression',
  'masterCompressionEnabled',
  'metronomeSettings',
  'practiceMetronome',
  'feelTiming',
  'channelLevels',
  'speedPct',
  'trainer',
  'repeatForm',
  'ending',
  'arrangementPolicy',

  // --- the band (see BAND_KEYS: these are also remembered per tune) ---
  ...BAND_KEYS,
  'melodyChannel',
  'leadSound',
  'bassSound',
  'guitarSound',
  'drumTimeStretch',

  // --- user content ---
  'alternativeShapes',
  'score',
  'savedCharts',
  'userPlaylists',
  'deletedPlaylists',
  'playlistLinks',
  'playlistAccount',
  'playlistDeletes',
  'accountSync',
  'deletedSavedTunes',
  'keyStamps',
  'setCursor',
  'autoAdvanceSet',
  'currentTuneRef',
  'currentTuneKey',
  'recentTunes',
  'tuneHistory',
  'tuneTaste',
  'tuneTasteRemoved',
  'otherHistory',
  'historyAccount',
  'tuneBands',
  'tuneFeels',
  'tuneArrangements',
  'tunePlayback',
  'houseDefaults',
  'playbackDisplaced',
  'writtenPlayback',
  'appliedHouseRevision',
  'playlistCallSource',
  'lastPlaylistAddedTo',
  'followFeelCues',
] as const;

export type PersistedKey = (typeof PERSISTED_KEYS)[number];

/**
 * What two open tabs SHARE: the user's documents and per-tune memory. Every
 * tab writes its whole persisted slice on any change, so without this a tab
 * holding an old copy overwrote a playlist another tab had just made (user-
 * found, Sep 2026). Each of these carries a change stamp (`keyStamps`), and a
 * tab adopts the other's copy of a key only when that copy is newer
 * (src/state/crossTab.ts). Everything else — the tune on screen, the tempo,
 * the view — stays each tab's own; on a reload the last tab to write wins.
 */
export const CROSS_TAB_KEYS = [
  'savedCharts',
  'deletedSavedTunes',
  'userPlaylists',
  'deletedPlaylists',
  'playlistLinks',
  'playlistAccount',
  'playlistDeletes',
  'recentTunes',
  'tuneHistory',
  'tuneTaste',
  'tuneTasteRemoved',
  'otherHistory',
  'historyAccount',
  'tuneBands',
  'tuneFeels',
  'tuneArrangements',
  'tunePlayback',
  'houseDefaults',
  'accountSync',
  'alternativeShapes',
  'preferredAlternativeIds',
] as const satisfies readonly PersistedKey[];

export type CrossTabKey = (typeof CROSS_TAB_KEYS)[number];

/** Copy exactly the listed keys off a state object. */
export function pickKeys<T extends object, K extends readonly (keyof T)[]>(
  state: T,
  keys: K,
): Pick<T, K[number]> {
  const out = {} as Pick<T, K[number]>;
  for (const key of keys) out[key] = state[key];
  return out;
}

/** What the persist middleware writes — derived, never hand-listed. */
export function persistedSlice<T extends Record<PersistedKey, unknown>>(
  state: T,
): Pick<T, PersistedKey> {
  return pickKeys(state, PERSISTED_KEYS as readonly PersistedKey[] as (keyof T)[]) as Pick<
    T,
    PersistedKey
  >;
}

/** The band subset out of any state that structurally carries it. */
export function pickBand<T extends StyleBand>(state: T): StyleBand {
  return pickKeys(state, BAND_KEYS as readonly (keyof StyleBand)[] as (keyof T)[]) as StyleBand;
}
